import {
  ArrayMaxSize,
  ArrayMinSize,
  ArrayUnique,
  IsArray,
  IsEmail,
  IsInt,
  IsOptional,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';
import { MAX_REASSIGNMENT_HOURS } from './federation.dtos';

// Upper bounds prevent fat-finger configurations (very long recipient lists,
// absurd windows). The DB mirrors both the lower and upper bounds with CHECK
// constraints (see DermloopFederationReassignmentAlert). `windowHours` reuses the
// shared one-year MAX_REASSIGNMENT_HOURS bound — the same business rule as the
// federation default and per-member reassignment intervals.
const MAX_RECIPIENT_EMAILS = 20;
const MAX_THRESHOLD_CASES = 10_000;

export interface FederationReassignmentAlertDto extends BaseDto {
  federationId: string;
  recipientEmails: string[];
  thresholdCases: number;
  windowHours: number;
  holidayListId?: string | null;
  // Carried as Date through the codebase (matches BaseDto's Date timestamps);
  // serialized as an ISO string at HTTP response time.
  lastFiredAt?: Date | null;
}

/** One previous assignee that cases were reassigned away from. */
export interface ReassignmentSenderDto {
  /**
   * Medical unit name, or the user's first and last name — database text, so
   * untranslated. `null` when the member resolves to neither, which is a data
   * defect: the caller supplies the user-facing fallback, since the querying
   * layer has no locale context.
   */
  name: string | null;
  /** Distinct cases this sender lost inside the window. */
  count: number;
}

/** Reassignment activity in a federation over the alert's trailing window. */
export interface FederationReassignmentSummaryDto {
  /**
   * Distinct cases reassigned in the window — a case reassigned twice counts
   * once. Per-sender counts are also per distinct case, so a case passed
   * between two senders is counted under both and the breakdown can sum to
   * more than this total.
   */
  count: number;
  /** Start of the window the count covers. */
  cutoff: Date;
  /** Busiest senders first, capped so the alert email stays readable. */
  senders: ReassignmentSenderDto[];
  /** Senders dropped by the cap. Their cases are still inside `count`. */
  otherSenderCount: number;
}

export class CreateFederationReassignmentAlertDto
  extends BaseCreateDto
  implements Partial<FederationReassignmentAlertDto>
{
  // federationId is taken from the URL path param by the controller, not the
  // request body, so it is intentionally absent here.
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_RECIPIENT_EMAILS)
  @ArrayUnique()
  @IsEmail({}, { each: true })
  recipientEmails: string[];

  @IsInt()
  @Min(1)
  @Max(MAX_THRESHOLD_CASES)
  thresholdCases: number;

  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  windowHours: number;

  @IsOptional()
  @IsUUID()
  holidayListId?: string | null;
}

export class UpdateFederationReassignmentAlertDto
  extends BaseUpdateDto
  implements Partial<FederationReassignmentAlertDto>
{
  @IsOptional()
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_RECIPIENT_EMAILS)
  @ArrayUnique()
  @IsEmail({}, { each: true })
  recipientEmails?: string[];

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_THRESHOLD_CASES)
  thresholdCases?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  windowHours?: number;

  @IsOptional()
  @IsUUID()
  holidayListId?: string | null;
}
